import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import api from '@/lib/api';

const defaultContent = {
    'despre-noi': {
        title: 'Despre noi',
        content: `
# Despre Colectarium

Colectarium este platforma de colecționare dedicată pasionaților de faleristică, numismatică și militare.

## Misiunea noastră

Creăm un spațiu sigur și de încredere pentru colecționari, unde pot:
- Vinde și cumpăra piese rare
- Participa la licitații
- Conecta cu alți pasionați

## Echipa

Suntem o echipă de colecționari și entuziaști tehnologie care înțeleg nevoile comunității.

## Contact

Pentru orice întrebări, ne puteți contacta la contact@colectarium.ro
        `
    },
    'cum-functioneaza': {
        title: 'Cum funcționează',
        content: `
# Cum funcționează Colectarium

## Pentru cumpărători

1. **Creează-ți un cont** - Înregistrarea este gratuită
2. **Caută piese** - Folosește filtrele pentru a găsi exact ce cauți
3. **Licitează sau cumpără** - Participă la licitații sau cumpără direct
4. **Plătește în siguranță** - Fondurile sunt protejate până la primirea piesei
5. **Confirmă primirea** - După verificare, fondurile sunt eliberate vânzătorului

## Pentru vânzători

1. **Listează-ți piesele** - Adaugă fotografii și descrieri detaliate
2. **Alege tipul de vânzare** - Licitație sau preț fix
3. **Comunică cu cumpărătorii** - Răspunde la întrebări
4. **Expediază rapid** - După confirmarea plății
5. **Primește banii** - După confirmarea livrării

## Protecție Plăți

Toate tranzacțiile sunt protejate prin sistemul nostru securizat:
- Cumpărătorul plătește
- Fondurile sunt ținute în siguranță
- Vânzătorul expediază
- Cumpărătorul confirmă primirea
- Fondurile sunt eliberate vânzătorului
        `
    },
    'termeni': {
        title: 'Termeni și condiții',
        content: `
# Termeni și Condiții

Ultima actualizare: Ianuarie 2026

## 1. Acceptarea termenilor

Prin utilizarea platformei Colectarium, acceptați acești termeni și condiții.

## 2. Utilizarea platformei

- Trebuie să aveți minimum 18 ani
- Informațiile furnizate trebuie să fie reale
- Sunteți responsabil pentru activitatea din contul dvs.

## 3. Vânzări și cumpărări

- Vânzătorii sunt responsabili pentru descrierea corectă a pieselor
- Cumpărătorii au dreptul la returnare în anumite condiții
- Platforma percepute comisioane pentru tranzacții finalizate

## 4. Comisioane

- 10% din valoarea tranzacției pentru vânzări sub 5000 RON
- 5% din valoarea tranzacției pentru vânzări peste 5000 RON

## 5. Conduită interzisă

- Fraudă sau înșelătorie
- Vânzarea de produse interzise
- Hărțuire sau comportament abuziv

## 6. Modificări

Ne rezervăm dreptul de a modifica acești termeni. Utilizatorii vor fi notificați.
        `
    },
    'confidentialitate': {
        title: 'Politica de confidențialitate',
        content: `
# Politica de Confidențialitate

Ultima actualizare: Ianuarie 2026

## 1. Datele colectate

- Informații de cont (email, nume, adresă)
- Date de tranzacție
- Preferințe și interese

## 2. Utilizarea datelor

- Furnizarea serviciilor
- Îmbunătățirea experienței
- Comunicări importante
- Securitatea platformei

## 3. Partajarea datelor

Nu vindem datele dvs. personale. Le partajăm doar:
- Cu acordul dvs.
- Pentru procesarea plăților
- Când legea o cere

## 4. Drepturile dvs.

- Acces la datele personale
- Rectificarea datelor
- Ștergerea contului
- Portabilitatea datelor

## 5. Securitate

Folosim măsuri tehnice și organizaționale pentru protejarea datelor.

## 6. Contact

Pentru întrebări: privacy@colectarium.ro
        `
    },
    'ajutor': {
        title: 'Ajutor',
        content: `
# Centru de Ajutor

## Întrebări frecvente

### Cum îmi creez un cont?
Click pe "Înregistrare" și completați formularul cu datele dvs.

### Cum listez o piesă de vânzare?
1. Autentificați-vă
2. Click pe "Listează"
3. Completați detaliile și adăugați fotografii
4. Alegeți tipul de vânzare (licitație sau preț fix)

### Cum particip la o licitație?
Găsiți piesa dorită, introduceți suma și click pe "Licitează".

### Cum funcționează plata?
Plata este ținută în siguranță până la confirmarea livrării.

### Ce fac dacă am o problemă cu comanda?
Contactați vânzătorul prin chat. Dacă nu se rezolvă, deschideți o dispută.

## Contact suport

Email: suport@colectarium.ro
Program: Luni-Vineri, 9:00-18:00
        `
    },
    'cookie-policy': {
        title: 'Politica de Cookie-uri',
        content: `
# Politica de Cookie-uri

Ultima actualizare: Ianuarie 2026

## Ce sunt cookie-urile?

Cookie-urile sunt fișiere text mici stocate pe dispozitivul dvs. când vizitați un site web.

## Cookie-uri utilizate

### Cookie-uri esențiale
- Autentificare și sesiune
- Securitate
- Funcționalități de bază

### Cookie-uri de performanță
- Analiza traficului
- Îmbunătățirea site-ului

### Cookie-uri de funcționalitate
- Preferințe salvate
- Personalizare

## Gestionarea cookie-urilor

Puteți controla cookie-urile din setările browserului:
- Chrome: Setări > Confidențialitate și securitate
- Firefox: Opțiuni > Confidențialitate
- Safari: Preferințe > Confidențialitate

## Mai multe informații

Pentru întrebări despre cookie-uri: privacy@colectarium.ro
        `
    }
};

const StaticPage = ({ slug: propSlug }) => {
    const { slug: paramSlug } = useParams();
    const slug = propSlug || paramSlug;
    const [content, setContent] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchContent = async () => {
            setLoading(true);
            try {
                // Try to fetch from API
                const res = await api.get(`/pages/${slug}`);
                if (res.data) {
                    setContent(res.data);
                } else {
                    // Use default content
                    setContent(defaultContent[slug] || { title: 'Pagină negăsită', content: 'Conținutul nu a fost găsit.' });
                }
            } catch (error) {
                // Use default content on error
                setContent(defaultContent[slug] || { title: 'Pagină negăsită', content: 'Conținutul nu a fost găsit.' });
            } finally {
                setLoading(false);
            }
        };

        fetchContent();
        window.scrollTo(0, 0);
    }, [slug]);

    if (loading) {
        return (
            <div className="min-h-screen bg-background py-8">
                <div className="container mx-auto px-4 lg:px-8 max-w-4xl">
                    <Skeleton className="h-12 w-64 mb-8" />
                    <Skeleton className="h-96" />
                </div>
            </div>
        );
    }

    // Simple markdown-like rendering
    const renderContent = (text) => {
        if (!text) return null;
        
        // Process inline formatting (bold, italic, links, inline code)
        const processInlineFormatting = (line) => {
            if (!line) return line;
            
            // Split by markdown patterns and process
            const parts = [];
            let remaining = line;
            let key = 0;
            
            while (remaining.length > 0) {
                // Check for links [text](url)
                const linkMatch = remaining.match(/\[([^\]]+)\]\(([^)]+)\)/);
                // Check for bold **text**
                const boldMatch = remaining.match(/\*\*([^*]+)\*\*/);
                // Check for italic *text* (but not **)
                const italicMatch = remaining.match(/(?<!\*)\*([^*]+)\*(?!\*)/);
                // Check for inline code `text`
                const codeMatch = remaining.match(/`([^`]+)`/);
                
                // Find the earliest match
                const matches = [
                    linkMatch ? { type: 'link', match: linkMatch, index: remaining.indexOf(linkMatch[0]) } : null,
                    boldMatch ? { type: 'bold', match: boldMatch, index: remaining.indexOf(boldMatch[0]) } : null,
                    italicMatch ? { type: 'italic', match: italicMatch, index: remaining.indexOf(italicMatch[0]) } : null,
                    codeMatch ? { type: 'code', match: codeMatch, index: remaining.indexOf(codeMatch[0]) } : null,
                ].filter(Boolean).sort((a, b) => a.index - b.index);
                
                if (matches.length === 0) {
                    // No more matches, add remaining text
                    if (remaining) parts.push(remaining);
                    break;
                }
                
                const earliest = matches[0];
                
                // Add text before the match
                if (earliest.index > 0) {
                    parts.push(remaining.slice(0, earliest.index));
                }
                
                // Add the formatted element
                if (earliest.type === 'link') {
                    parts.push(
                        <a 
                            key={key++} 
                            href={earliest.match[2]} 
                            className="text-primary hover:underline"
                            target={earliest.match[2].startsWith('http') ? '_blank' : undefined}
                            rel={earliest.match[2].startsWith('http') ? 'noopener noreferrer' : undefined}
                        >
                            {earliest.match[1]}
                        </a>
                    );
                } else if (earliest.type === 'bold') {
                    parts.push(<strong key={key++}>{earliest.match[1]}</strong>);
                } else if (earliest.type === 'italic') {
                    parts.push(<em key={key++}>{earliest.match[1]}</em>);
                } else if (earliest.type === 'code') {
                    parts.push(
                        <code key={key++} className="bg-muted px-1.5 py-0.5 rounded text-sm">
                            {earliest.match[1]}
                        </code>
                    );
                }
                
                // Continue with the rest of the string
                remaining = remaining.slice(earliest.index + earliest.match[0].length);
            }
            
            return parts.length > 0 ? parts : line;
        };
        
        return text.split('\n').map((line, i) => {
            // Headers
            if (line.startsWith('### ')) {
                return <h3 key={i} className="text-xl font-medium mt-4 mb-2">{processInlineFormatting(line.slice(4))}</h3>;
            }
            if (line.startsWith('## ')) {
                return <h2 key={i} className="text-2xl font-semibold mt-6 mb-3">{processInlineFormatting(line.slice(3))}</h2>;
            }
            if (line.startsWith('# ')) {
                return <h1 key={i} className="text-3xl font-bold mt-8 mb-4">{processInlineFormatting(line.slice(2))}</h1>;
            }
            // Horizontal rule
            if (line.trim() === '---' || line.trim() === '***') {
                return <hr key={i} className="my-6 border-border" />;
            }
            // List items
            if (line.startsWith('- ')) {
                return <li key={i} className="ml-4 mb-1 list-disc">{processInlineFormatting(line.slice(2))}</li>;
            }
            if (line.match(/^\d+\. /)) {
                return <li key={i} className="ml-4 mb-1 list-decimal">{processInlineFormatting(line.replace(/^\d+\. /, ''))}</li>;
            }
            // Empty lines
            if (line.trim() === '') {
                return <br key={i} />;
            }
            // Regular paragraphs
            return <p key={i} className="mb-2 text-muted-foreground">{processInlineFormatting(line)}</p>;
        });
    };

    return (
        <div className="min-h-screen bg-background py-8" data-testid="static-page">
            <div className="container mx-auto px-4 lg:px-8 max-w-4xl">
                <Card className="rounded-2xl">
                    <CardContent className="p-8">
                        <div className="prose prose-lg max-w-none">
                            {renderContent(content?.content)}
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default StaticPage;
